/**
 * React hook that wires the MQTT telemetry singleton to all status hooks.
 * Collects SIP, connectivity, battery, and permission state into heartbeats.
 * Sends alerts on critical state transitions.
 * Handles session enforcement (single-device login).
 */
import { useEffect, useRef, useState, useCallback, useSyncExternalStore } from 'react';
import { useAuth } from './useAuth';
import { useSip } from './useSip';
import { useConnectivity } from './useConnectivity';
import { useBattery } from './useBattery';
import { usePermissions } from './usePermissions';
import { NativeBridge, isNative } from '../native/NativeBridge';
import { KeepAliveBridge } from '../native/KeepAliveBridge';
import * as MqttTelemetry from '../services/MqttTelemetry';
import { connectWebSocketCommands, disconnectWebSocketCommands } from '../services/WebSocketCommands';

const MQTT_BROKER_URL = (import.meta as any).env?.VITE_MQTT_BROKER_URL || '';
const MQTT_USERNAME = (import.meta as any).env?.VITE_MQTT_USERNAME || '';
const MQTT_PASSWORD = (import.meta as any).env?.VITE_MQTT_PASSWORD || '';
const APP_VERSION = (import.meta as any).env?.VITE_APP_VERSION || '1.0.0';

export function useMqttTelemetry() {
  const { user, isAuthenticated, logout } = useAuth();
  const { sipState, sipExtension } = useSip();
  const { layers } = useConnectivity();
  const { battery } = useBattery();
  const { permissions, allGranted } = usePermissions();
  const [macAddress, setMacAddress] = useState('unknown');
  const [sessionConflict, setSessionConflict] = useState<string | null>(null);

  // Fetch device MAC address on mount (informational only, not used in topics)
  useEffect(() => {
    KeepAliveBridge.getDeviceMac().then(mac => {
      console.log('[MQTT] Device MAC/ID:', mac);
      setMacAddress(mac);
    });
  }, []);

  // Subscribe to MQTT connection state for re-renders
  const connectionState = useSyncExternalStore(
    MqttTelemetry.subscribe,
    MqttTelemetry.getConnectionState,
  );

  // Track previous states for alert detection
  const prevSipRef = useRef(sipState);
  const prevNetworkRef = useRef(layers.network);

  // Store connection type from native bridge
  const connectionTypeRef = useRef('unknown');

  // Periodically get connection type from native bridge
  useEffect(() => {
    const update = async () => {
      try {
        const status = await NativeBridge.getNetworkStatus();
        connectionTypeRef.current = status.connectionType;
      } catch { /* ignore */ }
    };
    update();
    const interval = setInterval(update, 30_000);
    return () => clearInterval(interval);
  }, []);

  // Set up the data collector so the singleton can gather status each heartbeat
  useEffect(() => {
    MqttTelemetry.setStatusDataCollector(() => {
      let status: 'ok' | 'degraded' | 'error' = 'ok';
      if (!layers.network) status = 'error';
      else if (!layers.api || sipState !== 'registered' || !allGranted) status = 'degraded';

      const missing: string[] = [];
      for (const [key, value] of Object.entries(permissions)) {
        if (value === 'denied' || value === 'prompt') {
          missing.push(key);
        }
      }

      return {
        status,
        services: {
          mqtt: { status: 'running' },
          network: { status: layers.network ? 'ok' : 'down' },
          sip: { status: sipState === 'registered' ? 'running' : sipState, extension: sipExtension || undefined },
        },
        system: {
          battery_level: battery.level,
          battery_charging: battery.charging,
          connection_type: connectionTypeRef.current,
        },
        permissions: {
          all_granted: allGranted,
          missing,
        },
      };
    });
  }, [sipState, sipExtension, layers, battery, permissions, allGranted]);

  // Handle session conflict — another device logged in as the same user
  const handleSessionConflict = useCallback((reason: string) => {
    console.warn('[MQTT] Session conflict detected, logging out:', reason);
    setSessionConflict(reason);
    setTimeout(() => {
      MqttTelemetry.disconnect();
      logout();
    }, 3000);
  }, [logout]);

  // Register session conflict listener
  useEffect(() => {
    const unsubscribe = MqttTelemetry.onSessionConflict(handleSessionConflict);
    return unsubscribe;
  }, [handleSessionConflict]);

  // Connect when authenticated + MQTT env var present, disconnect on logout
  const hasConnectedRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || !MQTT_BROKER_URL) {
      if (hasConnectedRef.current) {
        MqttTelemetry.sendAlert({
          type: 'logout',
          severity: 'warning',
          message: 'User logged out',
          service: 'app',
        });
        MqttTelemetry.disconnect();
        hasConnectedRef.current = false;
      }
      return;
    }

    if (hasConnectedRef.current) return;

    // Only need userId to connect (topics are userId-based)
    const userId = user?.id;
    if (!userId) return;

    hasConnectedRef.current = true;
    MqttTelemetry.connect({
      brokerUrl: MQTT_BROKER_URL,
      username: MQTT_USERNAME,
      password: MQTT_PASSWORD,
      userId: Number(userId),
      macAddress,
      sipExtension: sipExtension || 'unknown',
      appVersion: APP_VERSION,
      userName: user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : (user as any)?.username || 'Unknown',
      locationId: (user as any)?.locationId,
      locationName: (user as any)?.locationName,
    });
  }, [isAuthenticated, user, macAddress, sipExtension]);

  // Connect WebSocket commands for browser mode (fallback when MQTT not available)
  const wsConnectedRef = useRef(false);
  useEffect(() => {
    if (isNative) return; // Android/iOS uses MQTT
    if (!isAuthenticated) {
      if (wsConnectedRef.current) {
        disconnectWebSocketCommands();
        wsConnectedRef.current = false;
      }
      return;
    }

    const userId = user?.id;
    if (!userId || wsConnectedRef.current) return;

    wsConnectedRef.current = true;
    connectWebSocketCommands({
      userId: Number(userId),
      getToken: () => localStorage.getItem('caro_auth')
        ? JSON.parse(localStorage.getItem('caro_auth')!).accessToken
        : null,
    });

    return () => {
      disconnectWebSocketCommands();
      wsConnectedRef.current = false;
    };
  }, [isAuthenticated, user]);

  // Push SIP extension updates to MQTT config
  useEffect(() => {
    if (sipExtension && sipExtension !== 'unknown') {
      MqttTelemetry.updateSipExtension(sipExtension);
    }
  }, [sipExtension]);

  // Send alerts on critical state transitions
  useEffect(() => {
    if (prevSipRef.current === 'registered' && sipState === 'disconnected') {
      MqttTelemetry.sendAlert({
        type: 'service_down',
        severity: 'error',
        message: 'SIP registration lost',
        service: 'sip',
      });
    }
    prevSipRef.current = sipState;
  }, [sipState]);

  useEffect(() => {
    if (prevNetworkRef.current && !layers.network) {
      MqttTelemetry.sendAlert({
        type: 'offline',
        severity: 'critical',
        message: 'Network connectivity lost',
        service: 'network',
      });
    }
    prevNetworkRef.current = layers.network;
  }, [layers.network]);

  return { mqttState: connectionState, sessionConflict };
}
